import React, { useState, useEffect } from "react";
import moment from "moment";
import {
  Button,
  Col,
  Form,
  message,
  Modal,
  Row,
  Skeleton,
  Tooltip,
} from "antd";

import { useGetAdviseeSuggestions } from "apis/advisee";
import { useFetchConstant } from "apis/constant";

import FormDatePicker from "components/FormDatePicker/FormDatePicker";
import FormInput from "components/FormInput/FormInput";
import FormSelection from "components/FormSelection/FormSelection";
import SectionCard from "components/SectionCard/SectionCard";
import SectionContainer from "components/SectionContainer/SectionContainer";
import ClickableTextButton from "components/ClickableTextButton/ClickableTextButton";

import { TooltipIcon } from "./AddFamilyMemberModal.styles";
import FormInputNumber from "components/FormInputNumber/FormInputNumber";
import Locale from "react-intl-universal";

const { useForm } = Form;
const { confirm } = Modal;

const formatDefaultValues = (defaultValues) => ({
  ...defaultValues,
  dob: defaultValues.dob && moment(defaultValues.dob, "YYYY-MM-DD"),
  yearsOfResponsible:
    defaultValues.yearOfIndependence &&
    Number(defaultValues.yearOfIndependence) - moment().year(),
});

const AddFamilyMemberModal = ({
  visible,
  onClose,
  onOk,
  onRemove,
  defaultValues,
  adviseeId,
}) => {
  const [form] = useForm();

  const [hasSelectedLinkage, setHasSelectedLinkage] = useState(
    !!defaultValues.refAdviseeId
  );

  const [filterRelationShip, setFilterRelationShip] = useState([]);

  const {
    isLoading: isRelationshipsLoading,
    selection: relationships,
  } = useFetchConstant("relationships");
  const {
    isLoading: isAdviseeSuggestionsLoading,
    data: adviseeSuggestions,
  } = useGetAdviseeSuggestions(adviseeId, defaultValues.refAdviseeId);

  const isLoading = isRelationshipsLoading || isAdviseeSuggestionsLoading;

  useEffect(() => {
    console.log("relationships", relationships);

    return () => {};
  }, [relationships]);

  const handleOnOkButtonClick = async (e) => {
    e.preventDefault();
    try {
      const values = await form.validateFields();
      const newValues = {
        _id: defaultValues._id,
        ...values,
        dob: values.dob && values.dob.format("YYYY-MM-DD"),
        isDependent: !!values.yearsOfResponsible,
        yearOfIndependence:
          values.yearsOfResponsible &&
          moment().add(values.yearsOfResponsible, "year").format("YYYY"),
      };

      onOk(newValues);
    } catch (error) {
      error.errorFields.forEach((field) => message.error(field.errors[0]));
    }
  };

  const handleOnRemoveButtonClick = () => {
    confirm({
      title: "Do you want to remove this family member from the list?",
      okText: "Yes",
      onOk() {
        onRemove();
      },
      onCancel() {},
    });
  };

  const handleOnProfileLinkageChange = (selectedAdviseeId) => {
    const selectedAdvisee = adviseeSuggestions.find(
      (advisee) => advisee._id === selectedAdviseeId
    );
    if (selectedAdvisee) {
      setHasSelectedLinkage(true);
      form.setFieldsValue({
        name: selectedAdvisee.name,
        dob: moment(selectedAdvisee.dob),
      });
    }
  };

  const handleOnUnlinkButtonClick = () => {
    setHasSelectedLinkage(false);
    form.setFieldsValue({ refAdviseeId: undefined });
  };

  const footer = defaultValues
    ? [
        <Button key="remove" onClick={handleOnRemoveButtonClick}>
          {Locale.get("personalDetail.addFamilyMember.removeButton")}
        </Button>,
        <Button key="submit" type="primary" onClick={handleOnOkButtonClick}>
          {Locale.get("personalDetail.addFamilyMember.saveFamilyButton")}
        </Button>,
      ]
    : [
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleOnOkButtonClick}>
          Add this family member
        </Button>,
      ];

  return (
    <Modal
      title={Locale.get("personalDetail.addFamilyMember.modalTitle")}
      visible={visible}
      onOk={handleOnOkButtonClick}
      onCancel={onClose}
      footer={footer}
    >
      {isLoading ? (
        <Skeleton active />
      ) : (
        <Form form={form} initialValues={formatDefaultValues(defaultValues)}>
          <SectionContainer>
            <SectionCard
              title={Locale.get(
                "personalDetail.addFamilyMember.sectionCardTitle_0"
              )}
            >
              <FormSelection
                label={Locale.get("personalDetail.addFamilyMember.linkLabel")}
                name="refAdviseeId"
                selections={adviseeSuggestions}
                onChange={handleOnProfileLinkageChange}
              />
              {hasSelectedLinkage && (
                <ClickableTextButton
                  text={Locale.get(
                    "personalDetail.addFamilyMember.unlinkedAdvisee"
                  )}
                  onClick={handleOnUnlinkButtonClick}
                  buttonStyle={{ float: "right", marginTop: -15 }}
                />
              )}
            </SectionCard>
          </SectionContainer>
          <SectionCard
            title={Locale.get(
              "personalDetail.addFamilyMember.sectionCardTitle_1"
            )}
          >
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <FormInput
                    label={Locale.get("personalDetail.fName")}
                    name={["name", "firstName"]}
                    placeholder="John"
                    requiredErrorMessage={Locale.get(
                      "personalDetail.addFamilyMember.errMsgFName"
                    )}
                  />
                </Col>
                <Col span={12}>
                  <FormInput
                    label={Locale.get("personalDetail.lName")}
                    name={["name", "lastName"]}
                    placeholder="Doe"
                    requiredErrorMessage={Locale.get(
                      "personalDetail.addFamilyMember.errMsgLName"
                    )}
                  />
                </Col>
                <Col span={12}>
                  <FormSelection
                    label={Locale.get(
                      "personalDetail.addFamilyMember.relationship"
                    )}
                    name="relationship"
                    placeholder="Child"
                    requiredErrorMessage={Locale.get(
                      "personalDetail.addFamilyMember.errMsgRelationship"
                    )}
                    selections={relationships}
                  />
                </Col>
                <Col span={12}>
                  <FormDatePicker
                    label={Locale.get("personalDetail.dob")}
                    name="dob"
                    placeholder="1994-01-01"
                    requiredErrorMessage={Locale.get(
                      "personalDetail.addFamilyMember.errMsgDob"
                    )}
                    isDob
                  />
                </Col>
                <Col span={24}>
                  {/* <FormInput
                    label={
                      <span>
                        Number of Years left until Independence{" "}
                        <Tooltip
                          placement="topLeft"
                          title="Leave blank if this family member is not depending on the advisee."
                        >
                          <TooltipIcon />
                        </Tooltip>
                      </span>
                    }
                    name="yearsOfResponsible"
                    placeholder="20"
                  /> */}
                  <FormInputNumber
                    label={
                      <span>
                        {Locale.get("personalDetail.addFamilyMember.noOfYears")}{" "}
                        <Tooltip
                          placement="topLeft"
                          title={Locale.get(
                            "personalDetail.addFamilyMember.toolTipTitle"
                          )}
                        >
                          <TooltipIcon />
                        </Tooltip>
                      </span>
                    }
                    name="yearsOfResponsible"
                    placeholder="20"
                    maxValue={99}
                    min={0}
                  />
                </Col>
              </Row>
            </>
          </SectionCard>
        </Form>
      )}
    </Modal>
  );
};

export default AddFamilyMemberModal;
